// The Customers area's wire types — hand-authored against
// server/src/models/customers.rs and reviews.rs. Customer data is
// restaurant-scoped (/restaurants/{rid}/…); reviews and the order history
// are per store.

export type CustomerAddress = {
  addressId: string;
  label: string | null;
  addressLine: string;
  suburb: string;
  state: string;
  postcode: string;
  lat: number | null;
  lng: number | null;
  notes: string | null;
  isDefault: boolean;
};

export type AddressInput = Omit<CustomerAddress, 'addressId'>;

export type Customer = {
  customerId: string;
  userId: string | null;
  name: string;
  phoneE164: string | null;
  email: string | null;
  notes: string | null;
  marketingSmsOptIn: boolean;
  marketingEmailOptIn: boolean;
  addresses: CustomerAddress[];
  createdAt: string;
  updatedAt: string;
};

/** POST /customers — get-or-create: the phone is the identity. */
export type GetOrCreateCustomerInput = { phone: string; name: string | null; email: string | null };

/** PUT /customers/{id} — the whole profile. */
export type CustomerUpdate = {
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  marketingSmsOptIn: boolean;
  marketingEmailOptIn: boolean;
};

export type ImportRow = { name: string; phone: string; email: string | null };

/** The outbox's job: pending → running → completed (failed retries, dead gives up). */
export type ImportJob = {
  jobId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'dead' | string;
  rowCount: number;
  imported: number | null;
  skipped: number | null;
  lastError: string | null;
};

export type CouponType = 'flat_amount' | 'percent' | 'free_delivery';

export type Coupon = {
  couponId: string;
  code: string;
  couponType: CouponType;
  amountInCents: number | null;
  percentBp: number | null;
  startsOn: string | null;
  endsOn: string | null;
  appliesPickup: boolean;
  appliesDelivery: boolean;
  appliesDineIn: boolean;
  minOrderInCents: number | null;
  maxRedemptions: number | null;
  perCustomerLimit: number | null;
  firstOrderOnly: boolean;
  exclusive: boolean;
  active: boolean;
  allStores: boolean;
  storeIds: string[];
  /** ISO weekdays (1 = Monday); empty = every day. */
  daysOfWeek: number[];
  redemptionCount: number;
  createdAt: string;
  updatedAt: string;
};

/** POST /coupons and PUT /coupons/{id} take the whole document. */
export type CouponInput = Omit<Coupon, 'couponId' | 'redemptionCount' | 'createdAt' | 'updatedAt'>;

export type CouponRedemption = {
  redemptionId: string;
  customerId: string | null;
  storeId: string;
  orderId: string;
  redeemedAt: string;
};

export type RewardType = 'flat_amount' | 'percent';

/** GET returns null until the first PUT. */
export type LoyaltyProgram = {
  enabledPos: boolean;
  enabledOnline: boolean;
  ordersPerCard: number;
  rewardType: RewardType;
  posAmountInCents: number | null;
  onlineAmountInCents: number | null;
  posPercentBp: number | null;
  onlinePercentBp: number | null;
  accrueOnDiscounted: boolean;
  redeemOnDiscounted: boolean;
};

export type LoyaltyReward = {
  orderId: string;
  storeId: string;
  amountInCents: number;
  orderGrandTotalInCents: number;
  createdAt: string;
};

export type LoyaltyCard = {
  cardId: string;
  prevCardId: string | null;
  isOpen: boolean;
  redeemedOrderId: string | null;
  redeemedStoreId: string | null;
  redeemedAt: string | null;
  rewards: LoyaltyReward[];
  /** Σ rewards. */
  valueInCents: number;
  isRedeemable: boolean;
  ordersPerCard: number;
  createdAt: string;
};

export type Review = {
  reviewId: string;
  rating: number;
  comment: string | null;
  customerId: string | null;
  customerName: string | null;
  orderId: string | null;
  createdAt: string;
};

export type ReviewSettings = {
  enabled: boolean;
  googleReviewUrl: string | null;
};

/** "12.50" → 1250; blank/garbage → null. */
export const toCents = (dollars: string): number | null => {
  const v = parseFloat(dollars);
  return Number.isFinite(v) ? Math.round(v * 100) : null;
};

/** 1250 → "12.50" for form fields. */
export const toDollars = (cents: number | null): string => (cents === null ? '' : (cents / 100).toFixed(2));

/** "10" (percent) → 1000 basis points; blank/garbage → null. */
export const toBp = (percent: string): number | null => {
  const v = parseFloat(percent);
  return Number.isFinite(v) ? Math.round(v * 100) : null;
};

/** 1000 bp → "10" for form fields. */
export const fromBp = (bp: number | null): string => (bp === null ? '' : String(bp / 100));

/** blank → null, otherwise the integer. */
export const toIntOrNull = (v: string): number | null => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

/** The server's message for a thrown ApiError, or the fallback. */
export const messageOf = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);
